import React, { useState } from 'react';
import { signIn, fetchUserAttributes, signOut, confirmSignIn } from '@aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import toastr from 'toastr';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
  const [signInStep, setSignInStep] = useState(null);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const signInOutput = await signIn({ username: email, password });
      
      if (signInOutput.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setNeedsPasswordChange(true);
        setSignInStep(signInOutput);
        toastr.info('Please set your new password');
        setLoading(false);
        return;
      }
      
      if (!signInOutput.isSignedIn) {
        throw new Error('Sign in failed');
      }

      // Verify admin user
      const attributes = await fetchUserAttributes();
      const userType = attributes['custom:userType'];
      
      if (userType !== 'admin') {
        await signOut();
        toastr.error('Access denied. Admin access required.');
        return;
      }

      toastr.success('Admin login successful!');
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Login error:', error);
      toastr.error(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toastr.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toastr.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);

    try {
      const confirmOutput = await confirmSignIn({ challengeResponse: newPassword });
      
      if (!confirmOutput.isSignedIn) {
        throw new Error('Password change failed');
      }
      
      // Verify admin user after password change
      const attributes = await fetchUserAttributes();
      const userType = attributes['custom:userType'];
      
      if (userType !== 'admin') {
        await signOut();
        toastr.error('Access denied. Admin access required.');
        return;
      }

      toastr.success('Password changed successfully!');
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Password change error:', error);
      toastr.error(error.message || 'Password change failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid vh-100">
      <div className="row h-100">
        <div className="col-lg-8 d-flex align-items-center justify-content-center bg-primary text-white">
          <div className="text-center">
            <h1 className="display-4 fw-bold mb-4">ODIR Admin Portal</h1>
            <p className="lead fs-4">Manage MSPs and Global Configuration</p>
          </div>
        </div>

        <div className="col-lg-4 d-flex align-items-center">
          <div className="w-100 px-4">
            <div className="text-center mb-4">
              <h3>Admin Login</h3>
              <p className="text-muted">Sign in with admin credentials</p>
            </div>

            <form onSubmit={needsPasswordChange ? handlePasswordChange : handleLogin}>
              <div className="mb-3">
                <input
                  type="email"
                  className="form-control"
                  placeholder="Admin Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={needsPasswordChange}
                />
              </div>

              {!needsPasswordChange ? (
                <div className="mb-3">
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
              ) : (
                <>
                  <div className="alert alert-info">
                    <small>Please set a new password for your admin account</small>
                  </div>
                  <div className="mb-3">
                    <input
                      type="password"
                      className="form-control"
                      placeholder="New Password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <input
                      type="password"
                      className="form-control"
                      placeholder="Confirm New Password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                  </div>
                </>
              )}

              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={loading}
              >
                {loading ? 'Processing...' : (needsPasswordChange ? 'Set New Password' : 'Sign In')}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;